import { useId, type ReactNode } from 'react'

/**
 * Campo de formulário.
 *
 * Três escolhas que não são estéticas:
 *
 *  · O erro OCUPA o lugar da dica em vez de somar uma linha. Campo que cresce
 *    ao errar empurra o resto do formulário e faz a pessoa perder o lugar —
 *    pior ainda numa máquina que redesenha devagar.
 *  · Opcional é marcado no rótulo. O padrão de marcar o obrigatório com
 *    asterisco obriga a decorar a convenção; aqui, o que não diz nada é
 *    obrigatório, e o que é dispensável fala por si.
 *  · `aria-describedby` sempre aponta para o mesmo nó, que troca de conteúdo.
 *    Leitor de tela anuncia a mudança sem precisar de `aria-live`.
 */

interface PropsBase {
  rotulo: string
  dica?: ReactNode
  erro?: string | null
  opcional?: boolean
  largo?: boolean
}

interface PropsCampo extends PropsBase {
  valor: string
  aoMudar: (valor: string) => void
  aoSair?: () => void
  /* Data NÃO entra aqui: `<input type="date">` controlado zera o campo no meio
     da digitação, porque reporta valor vazio enquanto a data está incompleta.
     Para data existe `CampoData`, que combina máscara e calendário. */
  tipo?: 'text' | 'email' | 'tel'
  modo?: 'text' | 'email' | 'tel' | 'numeric'
  autoComplete?: string
  maxLength?: number
  destaque?: boolean
  placeholder?: string
}

export function Campo({
  rotulo,
  dica,
  erro,
  opcional,
  largo,
  valor,
  aoMudar,
  aoSair,
  tipo = 'text',
  modo,
  autoComplete,
  maxLength,
  destaque,
  placeholder,
}: PropsCampo) {
  const id = useId()
  const idAuxiliar = `${id}-aux`

  return (
    <div className={`campo${largo ? ' campo--largo' : ''}`}>
      <label className="campo__rotulo" htmlFor={id}>
        {rotulo}
        {opcional ? <span className="campo__opcional">opcional</span> : null}
      </label>
      <input
        id={id}
        className={`campo__entrada${destaque ? ' campo__entrada--chave' : ''}`}
        type={tipo}
        inputMode={modo}
        autoComplete={autoComplete}
        maxLength={maxLength}
        placeholder={placeholder}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        onBlur={aoSair}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro || dica ? idAuxiliar : undefined}
      />
      {erro ? (
        <p className="campo__erro" id={idAuxiliar}>
          <span aria-hidden="true">⚠</span>
          {erro}
        </p>
      ) : dica ? (
        <p className="campo__dica" id={idAuxiliar}>
          {dica}
        </p>
      ) : null}
    </div>
  )
}

interface PropsSelecao extends PropsBase {
  valor: string
  aoMudar: (valor: string) => void
  opcoes: Array<{ valor: string; texto: string }>
  vazio?: string
}

export function Selecao({
  rotulo,
  dica,
  erro,
  opcional,
  largo,
  valor,
  aoMudar,
  opcoes,
  vazio = 'Selecione',
}: PropsSelecao) {
  const id = useId()
  const idAuxiliar = `${id}-aux`

  return (
    <div className={`campo${largo ? ' campo--largo' : ''}`}>
      <label className="campo__rotulo" htmlFor={id}>
        {rotulo}
        {opcional ? <span className="campo__opcional">opcional</span> : null}
      </label>
      <select
        id={id}
        className="campo__selecao"
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro || dica ? idAuxiliar : undefined}
      >
        <option value="">{vazio}</option>
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.texto}
          </option>
        ))}
      </select>
      {erro ? (
        <p className="campo__erro" id={idAuxiliar}>
          <span aria-hidden="true">⚠</span>
          {erro}
        </p>
      ) : dica ? (
        <p className="campo__dica" id={idAuxiliar}>
          {dica}
        </p>
      ) : null}
    </div>
  )
}
